import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import { asColor } from '@atlassian/jira-issue-epic-color';
import { getUserShape } from '@atlassian/jira-issue-field-assignee/async';
import type { FieldValueServiceActions } from '@atlassian/jira-issue-field-base';
import {
	getParentShape,
	getHierarchyShape,
} from '@atlassian/jira-issue-field-parent-switcher/async';
import { getStatusShape } from '@atlassian/jira-issue-field-status/async';
import { isBaseHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies';
import {
	PRIORITY_TYPE,
	PARENT_TYPE,
	HIERARCHY_LEVEL_ABOVE,
} from '@atlassian/jira-platform-field-config';
import {
	TimeEstimateKey,
	StatusKey,
	AssigneeKey,
	SummaryKey,
} from '@atlassian/jira-providers-issue';
import type { aggBacklogDataQuery$data } from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import type {
	PrefetchedDataGql,
	SprintResponse,
	IssueParentGql,
} from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types';
import type { CardGql } from '../../../model/graphql';
import type { IssueParent } from '../../../model/issue-parent/types';
import { type Issue, hasEpicField } from '../../../model/issue/types';
import type { AggSoftwareCard, AggSoftwareCardParent } from '../../../model/types';
import type { NewCardGql, CardCreateGql } from '../../../rest/card-create-multiple-gql/types';
import type { JiraPlanDataRestResponse } from '../../../rest/fetch-plan-data/types';
import type { EpicIssueTypeState } from '../../../state/reducers/config/epic/types';
import { getIdFromAri } from '../../utils';
import type { FieldValues, Field, FieldConfigs } from './types';

const toFieldAccessibleValues = (fields: Field[] = []): FieldValues =>
	mapValues(
		keyBy(
			filter(fields, ({ isFieldAccessible }) => isFieldAccessible),
			({ fieldKey }) => fieldKey,
		),
		({ fieldValue }) => fieldValue,
	);

export const extractFieldValues = (
	{
		issue: { assignee, status, summary },
		priority,
		parentId,
		...restOfTheCard
	}: NewCardGql | CardGql,
	{ storyPointFieldId }: FieldConfigs,
	issueParents?: IssueParentGql[],
): FieldValues => {
	const estimate = 'estimate' in restOfTheCard ? restOfTheCard.estimate : null;

	// NOTE: Pre-populated fields should follow the same logic as how they are handled in syncing (src/packages/software/backlog/src/view/all-fields-sync/field-sync)
	const StoryPointKey: string = storyPointFieldId || '';

	const parentValue =
		parentId && issueParents?.length
			? issueParents.find(({ id }) => Number(id) === Number(parentId))
			: null;
	const issueParentType = issueParents?.length ? issueParents[0].issueType : null;

	const fields: Field[] = [
		{
			isFieldAccessible: Boolean(estimate),
			fieldKey: TimeEstimateKey,
			fieldValue: estimate?.originalEstimate?.value ?? null,
		},
		{
			isFieldAccessible: Boolean(StoryPointKey && estimate),
			fieldKey: StoryPointKey,
			fieldValue: estimate?.storyPoints ?? null,
		},
		{
			isFieldAccessible: Boolean(status && status.id),
			fieldKey: StatusKey,
			fieldValue: getStatusShape(status.id, status.name, status.category),
		},
		{
			isFieldAccessible: Boolean(assignee && assignee.accountId),
			fieldKey: AssigneeKey,
			fieldValue: getUserShape(
				assignee?.accountId || '',
				assignee?.displayName || '',
				assignee?.avatarUrl,
			),
		},
		{
			isFieldAccessible: Boolean(priority && priority.name),
			fieldKey: PRIORITY_TYPE,
			fieldValue: priority,
		},
		{
			isFieldAccessible: Boolean(parentValue && parentId),
			fieldKey: PARENT_TYPE,
			fieldValue: parentValue
				? getParentShape(parentValue.id, parentValue.key, parentValue.summary, {
						id: parentValue.issueType.id,
						name: parentValue.issueType.name,
						iconUrl: parentValue.issueType.iconUrl,
					})
				: null,
		},
		{
			isFieldAccessible: Boolean(issueParentType),
			fieldKey: HIERARCHY_LEVEL_ABOVE,
			fieldValue: issueParentType
				? getHierarchyShape({
						id: issueParentType.id,
						name: issueParentType.name,
						iconUrl: issueParentType.iconUrl,
					})
				: null,
		},
		{
			isFieldAccessible: Boolean(summary),
			fieldKey: SummaryKey,
			fieldValue: summary ?? null,
		},
	];

	return toFieldAccessibleValues(fields);
};

export const extractFetchIssueFieldValues = (
	issue: Issue,
	epicIssueType: EpicIssueTypeState | null,
): FieldValues => {
	const {
		summary,
		status,
		assigneeAccountId,
		assigneeName,
		avatarUrl,
		priorityName,
		priorityUrl,
		estimateStatistic,
	} = issue;

	const epicField =
		hasEpicField(issue) && isBaseHierarchyLevel(issue.typeHierarchyLevel) ? issue.epicField : null;

	const fields: Field[] = [
		{
			isFieldAccessible: Boolean(estimateStatistic?.statFieldId),
			fieldKey: estimateStatistic?.statFieldId ?? '',
			fieldValue: estimateStatistic?.statFieldValue?.value ?? null,
		},
		{
			isFieldAccessible: Boolean(status?.id),
			fieldKey: StatusKey,
			fieldValue: getStatusShape(status.id, status.name, status.statusCategory.key),
		},
		{
			isFieldAccessible: Boolean(assigneeAccountId && assigneeName && avatarUrl),
			fieldKey: AssigneeKey,
			fieldValue: getUserShape(assigneeAccountId || '', assigneeName || '', avatarUrl),
		},
		{
			isFieldAccessible: Boolean(priorityName && priorityUrl),
			fieldKey: PRIORITY_TYPE,
			fieldValue: { name: priorityName, iconUrl: priorityUrl },
		},
		{
			isFieldAccessible: Boolean(isBaseHierarchyLevel(issue.typeHierarchyLevel)),
			fieldKey: PARENT_TYPE,
			fieldValue: epicField
				? getParentShape(
						getIdFromAri(String(epicField.issueId)),
						epicField.epicKey,
						epicField.summary || epicField.text,
						{
							id: epicField.issueTypeId,
							name: epicField.label,
							iconUrl: epicField.issueTypeIconUrl,
						},
						asColor(epicField.color || epicField.epicColor),
					)
				: null,
		},
		{
			isFieldAccessible: Boolean(isBaseHierarchyLevel(issue.typeHierarchyLevel)),
			fieldKey: HIERARCHY_LEVEL_ABOVE,
			fieldValue:
				epicField || epicIssueType
					? getHierarchyShape({
							id: epicField?.issueTypeId ?? epicIssueType?.id,
							name: epicField?.label ?? epicIssueType?.name,
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							iconUrl: epicField?.issueTypeIconUrl ?? epicIssueType!.iconUrl,
						})
					: null,
		},
		{
			isFieldAccessible: Boolean(summary),
			fieldKey: SummaryKey,
			fieldValue: summary ?? null,
		},
	];

	return toFieldAccessibleValues(fields);
};

export const extractFieldValuesFromAgg = (
	card: NonNullable<AggSoftwareCard>,
	{ storyPointFieldId }: FieldConfigs,
	cardParents: AggSoftwareCardParent[] | null,
): FieldValues => {
	const estimate = 'estimate' in card ? card.estimate : null;

	// NOTE: Pre-populated fields should follow the same logic as how they are handled in syncing (src/packages/software/backlog/src/view/all-fields-sync/field-sync)
	const StoryPointKey: string = storyPointFieldId || '';

	const { status, assignee, priority, parentId, summary } = card;

	const parentValue =
		parentId && cardParents?.length
			? cardParents.filter(Boolean).find(({ id }) => id === parentId)
			: null;
	const filteredCardParents = cardParents?.filter(Boolean);
	const cardParentCardType = filteredCardParents?.length ? filteredCardParents[0].cardType : null;

	const fields: Field[] = [
		{
			isFieldAccessible: Boolean(estimate),
			fieldKey: TimeEstimateKey,
			fieldValue: estimate?.originalEstimate?.value ?? null,
		},
		{
			isFieldAccessible: Boolean(StoryPointKey && estimate),
			fieldKey: StoryPointKey,
			fieldValue: estimate?.storyPoints ?? null,
		},
		{
			isFieldAccessible: Boolean(status && status.id),
			fieldKey: StatusKey,
			// @ts-expect-error TS2322: Type 'string' is not assignable to type 'StatusCategoryKeys'.
			fieldValue: getStatusShape(status.id, status.name, status.category), // Check category
		},
		{
			isFieldAccessible: Boolean(assignee && assignee.accountId),
			fieldKey: AssigneeKey,
			fieldValue: getUserShape(assignee?.accountId || '', assignee?.name || '', assignee?.picture),
		},
		{
			isFieldAccessible: Boolean(priority && priority.name),
			fieldKey: PRIORITY_TYPE,
			fieldValue: priority,
		},
		{
			isFieldAccessible: Boolean(parentValue && parentId),
			fieldKey: PARENT_TYPE,
			fieldValue: parentValue
				? getParentShape(getIdFromAri(parentValue.id), parentValue.key, parentValue.summary, {
						id: parentValue.cardType.id,
						name: parentValue.cardType.name,
						iconUrl: parentValue.cardType.iconUrl,
					})
				: null,
		},
		{
			isFieldAccessible: Boolean(cardParentCardType),
			fieldKey: HIERARCHY_LEVEL_ABOVE,
			fieldValue: cardParentCardType
				? getHierarchyShape({
						id: cardParentCardType.id,
						name: cardParentCardType.name,
						iconUrl: cardParentCardType.iconUrl,
					})
				: null,
		},
		{
			isFieldAccessible: Boolean(summary),
			fieldKey: SummaryKey,
			fieldValue: summary ?? null,
		},
	];

	return toFieldAccessibleValues(fields);
};

export const mergeFieldsFromCardCreateData = (
	cardCreateData: CardCreateGql | null,
	{ mergeIssues }: FieldValueServiceActions,
	fieldConfigs: FieldConfigs,
	issueParents?: IssueParent[], // remove ? on FF cleanup
): void => {
	if (!cardCreateData?.planModeCardCreate) {
		return;
	}

	const { newCards } = cardCreateData.planModeCardCreate;

	if (newCards && newCards.length) {
		const issuesWithFields = newCards.map((card: NewCardGql) => {
			const fieldValues = extractFieldValues(card, fieldConfigs, issueParents);

			return { [card.issue.key]: fieldValues };
		});

		mergeIssues(issuesWithFields);
	}
};

export const mergeFieldsFromFetchIssuesData = (
	issues: Issue[] | null,
	{ mergeIssues }: FieldValueServiceActions,
	epicIssueType: EpicIssueTypeState | null,
): void => {
	if (issues && issues.length) {
		const issuesWithFields = issues.map((issue) => {
			const fieldValues = extractFetchIssueFieldValues(issue, epicIssueType);
			return { [issue.key]: fieldValues };
		});
		mergeIssues(issuesWithFields);
	}
};

export const mergeFieldsFromPrefetchedData = (
	prefetchedData: PrefetchedDataGql | null,
	{ mergeIssues }: FieldValueServiceActions,
	fieldConfigs: FieldConfigs,
): void => {
	if (!prefetchedData?.boardScope) {
		return;
	}

	const {
		backlog: { cards: backlogCards },
		board: { cards: boardCards, issueParents },
		sprints,
	} = prefetchedData.boardScope;

	const cards = sprints.reduce(
		(allCards: CardGql[], { cards: sprintCards }: SprintResponse) => [
			// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
			...allCards,
			...sprintCards,
		],
		[...backlogCards, ...boardCards],
	);

	if (cards.length) {
		const issuesWithFields = cards.map((card: CardGql) => {
			const fieldValues = extractFieldValues(card, fieldConfigs, issueParents);
			return { [card.issue.key]: fieldValues };
		});

		mergeIssues(issuesWithFields);
	}
};

export const mergeFieldsFromPrefetchedAggData = (
	prefetchedAggData: aggBacklogDataQuery$data | null,
	{ mergeIssues }: FieldValueServiceActions,
	fieldConfigs: FieldConfigs,
): void => {
	if (!prefetchedAggData) {
		return;
	}

	const {
		backlog,
		board: { cards: boardCards },
		sprints,
		cardParents,
	} = prefetchedAggData.boardScope;

	const backlogCards = backlog?.cards || [];
	const sprintCards = sprints.filter(Boolean).flatMap((sprint) => sprint.cards);
	const cards = [
		...backlogCards.filter(Boolean),
		...boardCards.filter(Boolean),
		...sprintCards.filter(Boolean),
	];

	if (cards.length) {
		const issuesWithFields = cards.map((card) => {
			const fieldValues = extractFieldValuesFromAgg(
				card,
				fieldConfigs,
				cardParents.filter(Boolean),
			);
			return { [card.key]: fieldValues };
		});

		mergeIssues(issuesWithFields);
	}
};

export const mergeFieldsFromPrefetchedJiraPlanData = (
	prefetchedJiraPlanRestData: JiraPlanDataRestResponse | null,
	fieldValueServiceActions: FieldValueServiceActions,
	epicIssueType: EpicIssueTypeState | null,
): void => {
	if (!prefetchedJiraPlanRestData) {
		return;
	}
	const { issues } = prefetchedJiraPlanRestData;
	mergeFieldsFromFetchIssuesData(issues, fieldValueServiceActions, epicIssueType);
};
